// Commercial Acoustic Calculator - RT60 Calculation Engine
(function () {
  "use strict";

  // Starter Quantity marker position between Existing and Recommended.
  // 0 = Existing, 0.5 = halfway, 1 = Recommended.
  const STARTER_QUANTITY_PERCENT = 0.6;

  function selectRestoredProductId(
    selectedProduct,
    wallProduct,
    ceilingProduct,
    validProductIds,
    wallMigrations,
    ceilingMigrations,
  ) {
    const migrate = (productId) =>
      wallMigrations[productId] || ceilingMigrations[productId] || productId;
    const validIds = new Set(validProductIds);
    const candidates = [
      migrate(selectedProduct),
      migrate(wallProduct),
      migrate(ceilingProduct),
      "9mm_felt_2x2",
    ];

    return (
      candidates.find((productId) => validIds.has(productId)) || "9mm_felt_2x2"
    );
  }

  function migrateLegacyProductSettings(
    settings,
    wallMigrations,
    ceilingMigrations,
  ) {
    const restored = { ...(settings || {}) };
    const migrateWall = (productId) => wallMigrations[productId] || productId;
    const migrateCeiling = (productId) =>
      ceilingMigrations[productId] || productId;
    const migrateAny = (productId) =>
      wallMigrations[productId] || ceilingMigrations[productId] || productId;
    const migrateRows = (rows, migrate, fallbackProductId) =>
      Array.isArray(rows)
        ? rows.map((row) => ({
            ...row,
            product_id: migrate(row?.product_id || fallbackProductId),
            quantity: Number(row?.quantity) || 0,
          }))
        : rows;

    if (restored.product_selection_mode === "build_your_space") {
      restored.product_selection_mode = "build";
    }

    restored.selected_product = migrateAny(restored.selected_product);
    restored.wall_product = migrateWall(restored.wall_product);
    restored.ceiling_product = migrateCeiling(restored.ceiling_product);
    restored.build_wall_products = migrateRows(
      restored.build_wall_products,
      migrateWall,
      restored.wall_product,
    );
    restored.build_ceiling_products = migrateRows(
      restored.build_ceiling_products,
      migrateCeiling,
      restored.ceiling_product,
    );
    restored.build_products = migrateRows(
      restored.build_products,
      (productId) => migrateAny(productId),
    );

    return restored;
  }

  window.AcousticCalculatorCommercialHelpers = {
    ...(window.AcousticCalculatorCommercialHelpers || {}),
    selectRestoredProductId,
    migrateLegacyProductSettings,
  };

  // Wait for data to be available
  function initializeApp() {
    if (typeof window.AcousticCalculatorData === "undefined") {
      console.error("AcousticCalculatorData is not loaded.");
      return;
    }

    if (typeof window.AcousticCalculatorCommercialData === "undefined") {
      console.error("AcousticCalculatorCommercialData is not loaded.");
      return;
    }

    const data = window.AcousticCalculatorData;
    const commercialData = window.AcousticCalculatorCommercialData;
    const WALL_PRODUCT_MIGRATION = {
      "9mm_2x2": "9mm_felt_2x2",
      "9mm_full_sheet": "9mm_felt_sheet",
      "18mm_2x2": "18mm_felt_2x2",
      "18mm_full_sheet": "18mm_felt_sheet",
      "3d_2x2": "apex_2x2",
      wood_slat_wall: "wood_slat_wall_2pack",
      "3d_blocks_sm_2": "3d_blocks_set8_12x12x2",
      "3d_blocks_md_2": "3d_blocks_set4_12x36x2",
      "3d_blocks_lg_2": "3d_blocks_set2_36x36x2",
      "3d_blocks_sm_4": "3d_blocks_set8_12x12x4",
      "3d_blocks_md_4": "3d_blocks_set4_12x36x4",
      "3d_blocks_lg_4": "3d_blocks_set2_36x36x4",
    };
    const CEILING_PRODUCT_MIGRATION = {
      cloud_baffles: "3d_clouds_4x4",
      wood_slat_ceiling: "wood_slat_ceiling_2x2",
      flat_clouds: "flat_clouds_2x4",
      blade_baffles: "blades_12oc",
      beams: "beams_12oc",
    };

    function computeShowSuperUser(sectionSettings) {
      if (sectionSettings.super_user_always_on) return true;
      const email = window.customerEmail || "";
      return email.endsWith("@feltright.com");
    }

    function buildSuperUserProducts(catalog) {
      const products = [];
      (catalog.feltRightProducts?.wall || []).forEach((p) => {
        products.push({ ...p, category: "wall", qty: 0 });
      });
      (catalog.feltRightProducts?.unlisted || []).forEach((p) => {
        products.push({ ...p, category: "unlisted", qty: 0 });
      });
      (catalog.feltRightProducts?.ceiling || []).forEach((p) => {
        products.push({ ...p, category: "ceiling", qty: 0 });
      });
      return products;
    }

    // Merge with existing Vue app configuration
    const existingApp = window.AcousticCalculatorApp || {};

    window.AcousticCalculatorApp = {
      // Keep existing configuration
      ...existingApp,
      data() {
        const baseData = existingApp.data ? existingApp.data() : {};

        // Only add commercial-specific properties if not already present
        return {
          ...baseData,
          ...data,

          // Add commercial-specific settings only if they don't exist
          settings: {
            ...baseData.settings,
            // Defaults from commercialData.defaultBuildingRoom.
            building:
              baseData.settings?.building ||
              commercialData.defaultBuildingRoom?.building ||
              "Business/Corporate",
            room:
              baseData.settings?.room ||
              commercialData.defaultBuildingRoom?.room ||
              "Open Office",
            walls:
              Array.isArray(baseData.settings?.walls) &&
              baseData.settings.walls.length === 4
                ? baseData.settings.walls.map((w) => ({
                    material_id: w?.material_id || "",
                  }))
                : [
                    { material_id: "" },
                    { material_id: "" },
                    { material_id: "" },
                    { material_id: "" },
                  ],
            ceiling_material:
              baseData.settings?.ceiling_material ||
              commercialData.ceilingMaterials?.[0]?.value,
            floor_material:
              baseData.settings?.floor_material ||
              commercialData.floorMaterials?.[0]?.value,
            floor_covering_id: baseData.settings?.floor_covering_id || "none",
            floor_covering_pct: baseData.settings?.floor_covering_pct ?? 0,
            available_wall_space: baseData.settings?.available_wall_space ?? 40,
            available_ceiling_space:
              baseData.settings?.available_ceiling_space ?? 70,
            open_entryways: baseData.settings?.open_entryways ?? 0,
            furniture: baseData.settings?.furniture || "partial",
            selected_product:
              baseData.settings?.selected_product || "9mm_felt_2x2",
            wall_product: baseData.settings?.wall_product || "9mm_felt_2x2",
            ceiling_product: baseData.settings?.ceiling_product || "beams_12oc",
            build_products: Array.isArray(baseData.settings?.build_products)
              ? baseData.settings.build_products
              : [],
            build_wall_products: Array.isArray(
              baseData.settings?.build_wall_products,
            )
              ? baseData.settings.build_wall_products
              : [
                  {
                    product_id:
                      baseData.settings?.wall_product || "9mm_felt_2x2",
                    quantity: 0,
                  },
                ],
            build_ceiling_products: Array.isArray(
              baseData.settings?.build_ceiling_products,
            )
              ? baseData.settings.build_ceiling_products
              : [
                  {
                    product_id:
                      baseData.settings?.ceiling_product || "beams_12oc",
                    quantity: 0,
                  },
                ],
            room_dividers_enabled:
              baseData.settings?.room_dividers_enabled || false,
            room_divider_quantity:
              baseData.settings?.room_divider_quantity ?? 0,
            product_selection_mode:
              baseData.settings?.product_selection_mode || "recommendation",
          },

          // Calculation results
          solutions: {
            wallsFirst: {
              wallQuantity: 0,
              ceilingQuantity: 0,
              wallSqFt: 0,
              ceilingSqFt: 0,
              rt60ByFrequency: {},
              bandsClosedCount: 0,
              bandsClosed: [false, false, false, false, false],
              meetsTarget: false,
              showContactSales: false,
            },
            balanced: {
              wallQuantity: 0,
              ceilingQuantity: 0,
              wallSqFt: 0,
              ceilingSqFt: 0,
              rt60ByFrequency: {},
              bandsClosedCount: 0,
              bandsClosed: [false, false, false, false, false],
              meetsTarget: false,
              showContactSales: false,
            },
            ceilingFirst: {
              wallQuantity: 0,
              ceilingQuantity: 0,
              wallSqFt: 0,
              ceilingSqFt: 0,
              rt60ByFrequency: {},
              bandsClosedCount: 0,
              bandsClosed: [false, false, false, false, false],
              meetsTarget: false,
              showContactSales: false,
            },
          },

          // Full recommended solutions at targetRT60 (slider scales these)
          recommendedSolutions: {
            wallsFirst: null,
            balanced: null,
            ceilingFirst: null,
          },

          // Frequency bands for calculations (must match data file format)
          frequencyBands: ["250 Hz", "500 Hz", "1000 Hz", "2000 Hz", "4000 Hz"],

          // RT60 Constant (0.049 for imperial units)
          RT60_CONSTANT: 0.049,

          // Maximum room volume limit (cubic feet)
          maxVolumeLimit: 35000,

          // Room volume effects (air absorption at different frequencies)
          roomVolumeEffects: {
            "250 Hz": 0,
            "500 Hz": 0,
            "1000 Hz": 1.2,
            "2000 Hz": 3,
            "4000 Hz": 7,
          },

          // Material and product references from commercial data file
          wallMaterials: commercialData.wallMaterials || [],
          ceilingMaterials: commercialData.ceilingMaterials || [],
          floorMaterials: commercialData.floorMaterials || [],
          // Filter products based on user authentication
          feltRightProducts: {
            wall: (commercialData.feltRightProducts?.wall || [])
              .filter((product) => {
                if (product.requiresAuth) {
                  const customerEmail = window.customerEmail || "";
                  return customerEmail.endsWith("@feltright.com");
                }
                return true;
              })
              .sort((a, b) =>
                a.name.localeCompare(b.name, undefined, { numeric: true }),
              ),
            ceiling: (commercialData.feltRightProducts?.ceiling || [])
              .slice()
              .sort((a, b) =>
                a.name.localeCompare(b.name, undefined, { numeric: true }),
              ),
          },
          roomTypes: commercialData.roomTypes || [],
          presets: commercialData.PRESETS || [],

          furnitureOptions: commercialData.furnitureOptions || [],
          floorCoverings: commercialData.floorCoverings || [],
          carpetTypes: commercialData.carpetTypes,
          furnitureAbsorption: commercialData.furnitureAbsorption,
          openEntrywayAbsorption: commercialData.openEntrywayAbsorption,

          // Frequency analysis
          existingAbsorptionByFrequency: {},
          requiredAbsorptionByFrequency: {},

          // Active solution tab
          activeSolutionTab: "balanced",

          // Loading states
          isCalculating: false,
          calculationProgress: 0,
          debounceTimer: null,
          _hashTimer: null,
          _reverbResizeTimer: null,
          _reverbResizeHandler: null,
          _documentKeydownHandler: null,

          // Current calc-ID (written to URL hash on every settings change)
          calcId: "",
          loadCalcInput: "",
          loadCalcError: "",

          // Validation
          validationErrors: [],

          // Reverb-bar slider — user's desired RT60 (seconds). Initialized
          // in mounted() to targetRT60 so the thumb starts at Recommended.
          desiredRT60: 0.6,

          // True while the user hovers or interacts with the reverb bar.
          reverbBarHovered: false,
          reverbBarDragging: false,

          // Reverb scroller label layout: real label widths + bar widths,
          // measured from the DOM after mount (fallback = Figma widths).
          reverbLabelWidths: {
            existing: 120,
            starter: 162,
            standard: 140,
            premium: 140,
          },
          reverbBarWidth: 1074,
          initialReverbBarWidth: 1074,

          showWallProductModal: false,
          wallProductModalTarget: null,
          unifiedProductModalOpener: null,

          reportModalOpen: false,
          reportSubmitting: false,
          reportForm: {
            name: "",
            email: "",
            phone: "",
            company: "",
            emailOptIn: false,
          },

          // Super-user / manual mode gate
          showSuperUser: false,

          // Super-user per-product quantities (all wall + unlisted + ceiling).
          // Each entry mirrors a catalog product with an added `qty` and `category`.
          superUserProducts: buildSuperUserProducts(commercialData),
          superUserRT60: {},
          superUserAvgRT60: 0,

          // Default settings for reset
          defaultSettings: {
            building:
              commercialData.defaultBuildingRoom?.building ||
              "Business/Corporate",
            room: commercialData.defaultBuildingRoom?.room || "Open Office",
            room_length: 30,
            room_width: 40,
            room_height: 10,
            walls: [
              { material_id: "" },
              { material_id: "" },
              { material_id: "" },
              { material_id: "" },
            ],
            ceiling_material: commercialData.ceilingMaterials?.[0]?.value,
            floor_material: commercialData.floorMaterials?.[0]?.value,
            floor_covering_id: "none",
            floor_covering_pct: 0,
            available_wall_space: 40,
            available_ceiling_space: 70,
            open_entryways: 0,
            furniture: "partial",
            selected_product: "9mm_felt_2x2",
            wall_product: "9mm_felt_2x2",
            ceiling_product: "beams_12oc",
            build_products: [],
            build_wall_products: [{ product_id: "9mm_felt_2x2", quantity: 0 }],
            build_ceiling_products: [{ product_id: "beams_12oc", quantity: 0 }],
            room_dividers_enabled: false,
            room_divider_quantity: 0,
            product_selection_mode: "recommendation",
          },

          // Solution explanation based on RT60 values
          solutionExplanation: "",
        };
      },

      computed: {
        // Merge existing computed properties
        ...(existingApp.computed || {}),
        // Room geometry calculations
        wallArea() {
          const length = parseFloat(this.settings.room_length) || 0;
          const width = parseFloat(this.settings.room_width) || 0;
          const height = parseFloat(this.settings.room_height) || 0;
          const totalWallArea = 2 * (length * height + width * height);
          // Subtract entryway area as per Google Sheets
          const entrywayArea = Math.round(this.settings.open_entryways * 18.67);
          return Math.max(0, totalWallArea - entrywayArea);
        },

        entrywayArea() {
          // Based on Google Sheets formula: =ROUND(B22*18.67,0)
          // Where B22 is the number of entryways
          return Math.round(this.settings.open_entryways * 18.67);
        },

        ceilingArea() {
          const length = parseFloat(this.settings.room_length) || 0;
          const width = parseFloat(this.settings.room_width) || 0;
          return length * width;
        },

        floorArea() {
          return this.ceilingArea;
        },

        floorCoveringsArea() {
          // Google Sheets formula: ROUND(B48*B21,0)
          // Where B48 is floor area and B21 is floor-covering percentage.
          return Math.round(
            this.floorArea * (this.settings.floor_covering_pct / 100),
          );
        },

        furnitureArea() {
          // Google Sheets formula: B48*B49
          // Where B48 is floor area and B49 is furniture multiplier
          const furniture = this.furnitureOptions.find(
            (f) => f.value === this.settings.furniture,
          );
          const multiplier = furniture
            ? furniture.floorCoveragePercent / 100
            : 0;
          return this.floorArea * multiplier;
        },

        roomVolume() {
          const length = parseFloat(this.settings.room_length) || 0;
          const width = parseFloat(this.settings.room_width) || 0;
          const height = parseFloat(this.settings.room_height) || 0;
          return length * width * height;
        },

        // Available space calculations
        availableWallSqFt() {
          return this.wallArea * (this.settings.available_wall_space / 100);
        },

        availableCeilingSqFt() {
          return (
            this.ceilingArea * (this.settings.available_ceiling_space / 100)
          );
        },

        // Target RT60 — delegates to the pure-function engine.
        targetRT60() {
          return window.AcousticCalculatorEngine.computeTargetRT60(
            this.settings,
            window.AcousticCalculatorCommercialData,
          );
        },

        // Sheet B30 = MAX(C87:F87) — max untreated RT60 across 500-4000 Hz.
        currentRT60() {
          const eng = window.AcousticCalculatorEngine;
          const ctx = this._engineCtx;
          var max = 0;
          ["500 Hz", "1000 Hz", "2000 Hz", "4000 Hz"].forEach(function (band) {
            var abs = eng.calculateExistingAbsorption(band, ctx);
            var rt =
              Math.round(eng.calculateRT60(ctx.roomVolume, abs) * 100) / 100;
            if (rt > max) max = rt;
          });
          return max;
        },

        effectiveTargetRT60() {
          return Math.round(this.targetRT60 * 100) / 100;
        },

        starterRT60() {
          var eng = window.AcousticCalculatorEngine;
          var ctx = this._engineCtx;
          if (!ctx || !ctx.wallProduct || !ctx.ceilingProduct) {
            return this.currentRT60;
          }
          var rec = this.recommendedSolutions;
          if (!rec.wallsFirst && !rec.balanced && !rec.ceilingFirst) {
            return this.currentRT60;
          }
          var solutions = [rec.wallsFirst, rec.balanced, rec.ceilingFirst];
          var bands = ["4000 Hz", "2000 Hz", "1000 Hz", "500 Hz"];
          var maxPerSolution = solutions.map(function (sol) {
            if (!sol) return 0;
            var wallQ = Math.floor(
              (sol.wallQuantity || 0) * STARTER_QUANTITY_PERCENT,
            );
            var ceilQ = Math.floor(
              (sol.ceilingQuantity || 0) * STARTER_QUANTITY_PERCENT,
            );
            var maxRT = 0;
            bands.forEach(function (freq) {
              var rt = eng.calculateRT60WithProducts(freq, wallQ, ceilQ, ctx);
              if (rt > maxRT) maxRT = rt;
            });
            return maxRT;
          });
          var validValues = maxPerSolution.filter(function (v) {
            return v > 0;
          });
          if (validValues.length === 0) return this.currentRT60;
          var sum = validValues.reduce(function (a, b) {
            return a + b;
          }, 0);
          return sum / validValues.length;
        },

        barScale() {
          return window.AcousticCalculatorEngine.computeBarScale(
            this.effectiveTargetRT60,
            this.currentRT60,
          );
        },

        reverbBarPositions() {
          var eng = window.AcousticCalculatorEngine;
          return {
            existingPct: eng.rtToPct(this.currentRT60),
            starterPct: eng.rtToPct(
              this.singleProductMarkerTiers.starter?.rt60 || 0,
            ),
            standardPct: eng.rtToPct(
              this.singleProductMarkerTiers.standard?.rt60 || 0,
            ),
            premiumPct: eng.rtToPct(
              this.singleProductMarkerTiers.premium?.rt60 || 0,
            ),
            targetPct: eng.rtToPct(this.desiredRT60),
          };
        },

        recommendationReverbMarkers() {
          var pos = this.reverbBarPositions;
          return [
            {
              key: "existing",
              pct: pos.existingPct,
              label: "Existing<br>Reverb Level",
              value: Number(this.currentRT60 || 0).toFixed(2) + " sec",
            },
            {
              key: "starter",
              pct: pos.starterPct,
              label: "Reverb Level<br>With Starter<br>Quantity",
              value: Number(this.starterRT60 || 0).toFixed(2) + " sec",
            },
            {
              key: "recommended",
              pct: pos.recommendedPct,
              label: "Recommended<br>Quantity",
              value: Number(this.effectiveTargetRT60 || 0).toFixed(2) + " sec",
            },
            {
              key: "target",
              pct: pos.targetPct,
              label: "Target<br>Reverb Level",
              value: Number(this.desiredRT60 || 0).toFixed(2) + " sec",
            },
          ];
        },

        mergedMarkers() {
          var labels = {
            starter: "Starter<br>Quantity",
            standard: "Standard<br>Quantity",
            premium: "Premium<br>Quantity",
            existing: "Existing<br>Reverb Level",
          };
          var values = {
            starter:
              Number(this.singleProductMarkerTiers.starter?.rt60 || 0).toFixed(
                2,
              ) + " sec",
            standard:
              Number(this.singleProductMarkerTiers.standard?.rt60 || 0).toFixed(
                2,
              ) + " sec",
            premium:
              Number(this.singleProductMarkerTiers.premium?.rt60 || 0).toFixed(
                2,
              ) + " sec",
            existing: Number(this.currentRT60 || 0).toFixed(2) + " sec",
          };
          var positions = this.reverbBarPositions;
          var markers = ["existing", "starter", "standard", "premium"].map(
            function (key) {
              return {
                key: key,
                pct: positions[key + "Pct"],
                label: labels[key],
                value: values[key],
              };
            },
          );
          return this.mergeCoincidentReverbMarkers(markers);
        },

        initialReverbMarkers() {
          var eng = window.AcousticCalculatorEngine;
          return [
            {
              key: "existing",
              pct: eng.rtToPct(this.currentRT60),
              label: "Existing<br>Reverb Level",
              value: Number(this.currentRT60 || 0).toFixed(2) + " sec",
            },
            {
              key: "target",
              pct: eng.rtToPct(this.effectiveTargetRT60),
              label: "Target<br>Reverb Level",
              value: Number(this.effectiveTargetRT60 || 0).toFixed(2) + " sec",
            },
          ];
        },

        initialReverbMarkerOffsets() {
          return this.stackReverbMarkers(this.initialReverbMarkers);
        },

        markerLabelOffsets() {
          return this.stackReverbMarkers(this.mergedMarkers);
        },

        // Multi Product keeps the legacy read-only result: Existing and Ideal
        // are always shown, and New appears once the user enters a quantity.
        buildReverbMarkers() {
          var positions = this.buildModeReverbBarPositions;
          var values = this.buildModeReverbBarValueLabels;
          var markers = [
            {
              key: "existing",
              pct: positions.existingPct,
              label: "Existing<br>Reverb Level",
              value: values.existing,
            },
            {
              key: "ideal",
              pct: positions.idealPct,
              label: "Ideal<br>Reverb Level",
              value: values.ideal,
            },
          ];
          if (this.hasBuildModeQuantity) {
            markers.push({
              key: "new",
              pct: positions.newLevelPct,
              label: "New<br>Reverb Level",
              value: values.newLevel,
            });
          }
          return markers;
        },

        buildReverbMarkerOffsets() {
          return this.stackReverbMarkers(this.buildReverbMarkers);
        },

        // Build-mode RT60 with user-selected products. Uses the same
        // 500-4000 Hz max-band convention as currentRT60.
        buildModeRT60() {
          var max = 0;
          ["500 Hz", "1000 Hz", "2000 Hz", "4000 Hz"].forEach((band) => {
            var rt =
              Math.round(this.calculateBuildModeRT60(band, 1) * 100) / 100;
            if (rt > max) max = rt;
          });
          return max;
        },

        buildModeTierRT60s() {
          var engine = window.AcousticCalculatorEngine;
          var ctx = this._engineCtx;
          var targetAbsorption = engine.calculateRequiredAbsorption(
            ctx.roomVolume,
            this.effectiveTargetRT60,
          );
          var calculateReferenceTier = (fraction) => {
            var max = 0;
            ["500 Hz", "1000 Hz", "2000 Hz", "4000 Hz"].forEach((band) => {
              var existing = engine.calculateExistingAbsorption(band, ctx);
              var treatment = Math.max(targetAbsorption - existing, 0);
              var rt =
                Math.round(
                  engine.calculateRT60(
                    ctx.roomVolume,
                    existing + treatment * fraction,
                  ) * 100,
                ) / 100;
              if (rt > max) max = rt;
            });
            return max;
          };
          return {
            premium: calculateReferenceTier(1),
            standard: calculateReferenceTier(0.7),
            starter: calculateReferenceTier(0.4),
          };
        },

        buildModeNormalizedRows() {
          var rows = window.AcousticCalculatorProductTiers.normalizeBuildRows(
            this.settings,
            this.combinedProducts,
          );
          var productsById = this.combinedProducts.reduce(function (
            byId,
            product,
          ) {
            byId[product.id] = product;
            return byId;
          }, {});

          return rows.map(function (row) {
            var product = productsById[row.product_id];
            return {
              productId: product.id,
              name: product.name,
              url: product.url,
              surface: row.surface,
              quantity: row.quantity,
            };
          });
        },

        hasBuildModeQuantity() {
          return (this.settings.build_products || []).some(
            (row) => (Number(row.quantity) || 0) > 0,
          );
        },

        buildModeReverbBarPositions() {
          var engine = window.AcousticCalculatorEngine;
          return {
            existingPct: engine.rtToPct(this.currentRT60),
            idealPct: engine.rtToPct(this.effectiveTargetRT60),
            newLevelPct: engine.rtToPct(this.buildModeRT60),
          };
        },

        buildModeReverbBarValueLabels() {
          return {
            existing: Number(this.currentRT60 || 0).toFixed(2) + " sec",
            ideal: Number(this.effectiveTargetRT60 || 0).toFixed(2) + " sec",
            newLevel: Number(this.buildModeRT60 || 0).toFixed(2) + " sec",
          };
        },

        buildModeResultAlignedStyle() {
          var pct = this.buildModeReverbBarPositions.newLevelPct;
          var insetCorrection = 9 - pct * 0.18;
          return "left: calc(" + pct + "% + " + insetCorrection + "px);";
        },

        reverbBarThumbPct() {
          return window.AcousticCalculatorEngine.rtToPct(this.desiredRT60);
        },

        reverbBarThumbAlignedStyle() {
          var pct = this.reverbBarThumbPct;
          // The marker row's 13px outer inset resolves to the bar's 9px
          // content inset after its border/padding are accounted for.
          var insetCorrection = 9 - pct * 0.18;
          return "left: calc(" + pct + "% + " + insetCorrection + "px);";
        },

        reverbSliderMin() {
          var premium = this.singleProductMarkerTiers.premium?.rt60;
          var lowerBound = Number.isFinite(premium)
            ? premium
            : this.effectiveTargetRT60;
          return Math.min(lowerBound, this.currentRT60);
        },

        reverbSliderMax() {
          var premium = this.singleProductMarkerTiers.premium?.rt60;
          var lowerBound = Number.isFinite(premium)
            ? premium
            : this.effectiveTargetRT60;
          return Math.max(lowerBound, this.currentRT60);
        },

        reverbScaleSegments() {
          return window.AcousticCalculatorEngine.computeReverbScaleSegments();
        },

        reverbBarTicks() {
          return window.AcousticCalculatorEngine.BAR_SCALE_TICKS.map(
            (tick, index) => {
              return {
                value: tick,
                label:
                  tick.toFixed(index === 0 ? 0 : 1) +
                  (index ===
                  window.AcousticCalculatorEngine.BAR_SCALE_TICKS.length - 1
                    ? "+ sec"
                    : " sec"),
                pct: window.AcousticCalculatorEngine.rtToPct(tick),
              };
            },
          );
        },

        // Shared engine context for Vue methods that delegate to the engine.
        // Vue 3 caches computed props, so all delegations share one build per change.
        _engineCtx() {
          return window.AcousticCalculatorEngine.buildContext(
            this.settings,
            window.AcousticCalculatorCommercialData,
          );
        },

        combinedProducts() {
          return window.AcousticCalculatorProductTiers.normalizeCatalog(
            this.feltRightProducts,
          );
        },

        selectedProduct() {
          return this.combinedProducts.find(
            (product) => product.id === this.settings.selected_product,
          );
        },

        selectedProductSurface() {
          return this.selectedProduct?.surfaces?.[0] || null;
        },

        singleProductPremiumSolution() {
          const surface = this.selectedProductSurface;
          if (!surface || !this.selectedProduct) {
            return window.AcousticCalculatorEngine.createEmptySolution();
          }
          const engineSettings = { ...this.settings };
          engineSettings[`${surface}_product`] = this.selectedProduct.id;
          const ctx = window.AcousticCalculatorEngine.buildContext(
            engineSettings,
            window.AcousticCalculatorCommercialData,
          );
          return window.AcousticCalculatorEngine.calculateSingleSurfaceSolution(
            surface,
            this.desiredRT60,
            ctx,
          );
        },

        singleProductExceedsAvailableSpace() {
          const surface = this.selectedProductSurface;
          const product = this.selectedProduct;
          const selection = this.singleProductSelection;
          if (!surface || !product || !selection) return false;
          const required =
            (Number(selection.quantity) || 0) *
            (Number(product.footprint) || 0);
          const available =
            surface === "wall"
              ? Number(this.availableWallSqFt) || 0
              : Number(this.availableCeilingSqFt) || 0;
          return required > available;
        },

        recommendationShowsContactSales() {
          return Boolean(this.solutions?.wallsFirst?.showContactSales);
        },

        singleProductTiers() {
          return this.singleProductMarkerTiers;
        },

        singleProductSelection() {
          const surface = this.selectedProductSurface;
          const product = this.selectedProduct;
          if (!surface || !product) return null;

          const engine = window.AcousticCalculatorEngine;
          const engineSettings = { ...this.settings };
          engineSettings[`${surface}_product`] = product.id;
          const ctx = engine.buildContext(
            engineSettings,
            window.AcousticCalculatorCommercialData,
          );
          const premiumQuantity =
            this.singleProductMarkerTiers.premium.quantity;

          return window.AcousticCalculatorProductTiers.buildSelectionResult(
            premiumQuantity,
            this.desiredRT60,
            (quantity) =>
              Object.fromEntries(
                engine.BANDS.map((frequency) => [
                  frequency,
                  engine.calculateRT60WithProducts(
                    frequency,
                    surface === "wall" ? quantity : 0,
                    surface === "ceiling" ? quantity : 0,
                    ctx,
                  ),
                ]),
              ),
            this.singleProductMarkerTiers,
          );
        },

        // Fixed reference tiers for the reverb ruler. These are based on the
        // recommended room target, not the thumb's live desired value.
        singleProductMarkerTiers() {
          const surface = this.selectedProductSurface;
          const product = this.selectedProduct;
          if (!surface || !product) return {};

          const engine = window.AcousticCalculatorEngine;
          const engineSettings = { ...this.settings };
          engineSettings[`${surface}_product`] = product.id;
          const ctx = engine.buildContext(
            engineSettings,
            window.AcousticCalculatorCommercialData,
          );
          const referenceSolution = engine.calculateSingleSurfaceSolution(
            surface,
            this.effectiveTargetRT60,
            ctx,
          );
          const premiumQuantity = referenceSolution[`${surface}Quantity`];

          return window.AcousticCalculatorProductTiers.buildTierResults(
            premiumQuantity,
            (quantity) =>
              Object.fromEntries(
                engine.BANDS.map((frequency) => [
                  frequency,
                  engine.calculateRT60WithProducts(
                    frequency,
                    surface === "wall" ? quantity : 0,
                    surface === "ceiling" ? quantity : 0,
                    ctx,
                  ),
                ]),
              ),
            this.buildModeTierRT60s,
          );
        },

        // Get selected products
        selectedWallProduct() {
          return this.feltRightProducts.wall.find(
            (p) => p.id === this.settings.wall_product,
          );
        },

        selectedCeilingProduct() {
          return this.feltRightProducts.ceiling.find(
            (p) => p.id === this.settings.ceiling_product,
          );
        },

        // Get selected materials. Returns the material for wall 1 for any
        // legacy reference paths; per-wall material lookup happens inside the
        // engine via the walls[] array.
        selectedWallMaterial() {
          const id = this.settings.walls?.[0]?.material_id;
          return id ? this.wallMaterials.find((m) => m.value === id) : null;
        },

        selectedCeilingMaterial() {
          return this.ceilingMaterials.find(
            (m) => m.value === this.settings.ceiling_material,
          );
        },

        selectedFloorMaterial() {
          return this.floorMaterials.find(
            (m) => m.value === this.settings.floor_material,
          );
        },

        // Get furniture coverage
        furnitureCoverage() {
          const furniture = this.furnitureOptions.find(
            (f) => f.value === this.settings.furniture,
          );
          return furniture ? furniture.percentage / 100 : 0;
        },

        currentRoom() {
          const rooms = commercialData.ROOM_TYPES || [];
          return rooms.find(
            (r) =>
              r.building === this.settings.building &&
              r.room === this.settings.room,
          );
        },

        // Warning checks
        shouldShowCriticalAudioWarning() {
          return !!(this.currentRoom && this.currentRoom.critical_audio);
        },

        shouldShowMechanicalNoiseWarning() {
          return !!(this.currentRoom && this.currentRoom.mechanical_noise);
        },

        // Unique list of building names, ordered by the catalog's buildingTypes
        // (sheet-authored order), filtered to those actually present in ROOM_TYPES.
        buildingsList() {
          const rooms = commercialData.ROOM_TYPES || [];
          const present = new Set(rooms.map((r) => r.building));
          const ordered = (commercialData.buildingTypes || []).filter((b) =>
            present.has(b),
          );
          // Catch any building present in ROOM_TYPES but missing from buildingTypes.
          rooms.forEach((r) => {
            if (!ordered.includes(r.building)) ordered.push(r.building);
          });
          return ordered;
        },

        // Rooms available for the currently-selected building, in catalog order.
        roomsForBuilding() {
          const rooms = commercialData.ROOM_TYPES || [];
          return rooms
            .filter((r) => r.building === this.settings.building)
            .map((r) => r.room);
        },

        shouldShowHighCeilingWarning() {
          return this.settings.room_height > 12;
        },

        shouldShowLargeRoomWarning() {
          return this.roomVolume > 35000;
        },

        shouldShowExceedSpaceWarning() {
          // Excel formula: =IFERROR(IF(OR(E35>E34, E36>E34, E37>E34, G35>G34, G36>G34, G37>G34), A108,""),"")
          // E35,E36,E37 = wall needed for solutions 1,2,3 (wallsFirst, balanced, ceilingFirst)
          // G35,G36,G37 = ceiling needed for solutions 1,2,3
          // E34 = available wall space, G34 = available ceiling space

          if (
            !this.solutions.wallsFirst &&
            !this.solutions.balanced &&
            !this.solutions.ceilingFirst
          ) {
            return false;
          }

          // Check if ANY solution's wall or ceiling square footage exceeds available space
          // E35-E37 = wallQuantity * productFootprint, G35-G37 = ceilingQuantity * productFootprint
          const wallsFirstWallExceeds =
            (this.solutions.wallsFirst?.wallSqFt || 0) > this.availableWallSqFt;
          const balancedWallExceeds =
            (this.solutions.balanced?.wallSqFt || 0) > this.availableWallSqFt;
          const ceilingFirstWallExceeds =
            (this.solutions.ceilingFirst?.wallSqFt || 0) >
            this.availableWallSqFt;

          const wallsFirstCeilingExceeds =
            (this.solutions.wallsFirst?.ceilingSqFt || 0) >
            this.availableCeilingSqFt;
          const balancedCeilingExceeds =
            (this.solutions.balanced?.ceilingSqFt || 0) >
            this.availableCeilingSqFt;
          const ceilingFirstCeilingExceeds =
            (this.solutions.ceilingFirst?.ceilingSqFt || 0) >
            this.availableCeilingSqFt;

          const exceeds =
            wallsFirstWallExceeds ||
            balancedWallExceeds ||
            ceilingFirstWallExceeds ||
            wallsFirstCeilingExceeds ||
            balancedCeilingExceeds ||
            ceilingFirstCeilingExceeds;

          return exceeds;
        },

        shouldShowInadequateAbsorptionWarning() {
          // Check if any solution has RT60 > 3 seconds at 250 Hz
          const wallsFirstRT60 =
            this.solutions.wallsFirst?.rt60ByFrequency?.["250 Hz"] || 0;
          const balancedRT60 =
            this.solutions.balanced?.rt60ByFrequency?.["250 Hz"] || 0;
          const ceilingFirstRT60 =
            this.solutions.ceilingFirst?.rt60ByFrequency?.["250 Hz"] || 0;

          return wallsFirstRT60 > 3 || balancedRT60 > 3 || ceilingFirstRT60 > 3;
        },

        warnings() {
          const w = [];
          const msgs = commercialData.warnings || {};
          if (this.shouldShowCriticalAudioWarning) {
            w.push({
              key: "critical-audio",
              severity: "warning",
              message: msgs.criticalAudio?.message,
            });
          }
          if (this.shouldShowMechanicalNoiseWarning) {
            w.push({
              key: "mechanical-noise",
              severity: "warning",
              message: msgs.mechanicalNoise?.message,
            });
          }
          if (this.shouldShowHighCeilingWarning) {
            w.push({
              key: "high-ceiling",
              severity: "warning",
              message: msgs.highCeiling?.message,
            });
          }
          if (this.shouldShowLargeRoomWarning) {
            w.push({
              key: "large-room",
              severity: "warning",
              message: msgs.largeRoom?.message,
            });
          }
          if (this.shouldShowExceedSpaceWarning) {
            w.push({
              key: "exceed-space",
              severity: "warning",
              message: msgs.exceededSpace?.message,
            });
          }
          if (this.shouldShowInadequateAbsorptionWarning) {
            w.push({
              key: "inadequate-absorption",
              severity: "caution",
              message: msgs.inadequateAbsorption?.message,
            });
          }
          return w;
        },

        // Check if ANY solution exceeds available space - if so, hide all cards
        anySolutionExceedsSpace() {
          // Check each solution to see if it needs more space than available
          const solutions = [
            this.solutions.wallsFirst,
            this.solutions.balanced,
            this.solutions.ceilingFirst,
          ];

          return solutions.some((solution) => {
            if (!solution) return false;
            const wallExceeds = solution.wallNeeded > this.availableWallSqFt;
            const ceilingExceeds =
              solution.ceilingNeeded > this.availableCeilingSqFt;
            return wallExceeds || ceilingExceeds;
          });
        },

        // Check if volume exceeds limit
        exceedsVolumeLimit() {
          return this.roomVolume > this.maxVolumeLimit;
        },
      },

      methods: {
        // Merge existing methods
        ...(existingApp.methods || {}),

        syncSelectedProductSettings() {
          const surface = this.selectedProductSurface;
          if (surface && this.selectedProduct) {
            this.settings[`${surface}_product`] = this.selectedProduct.id;
          }
        },

        onSingleProductSelection() {
          this.syncSelectedProductSettings();
          this.storeSettings();
          this.calculateSolutions();
        },

        openUnifiedProductModal(mode, index, event) {
          this.wallProductModalTarget = {
            mode: mode,
            index: Number.isInteger(index) ? index : null,
          };
          this.unifiedProductModalOpener = event?.currentTarget || null;
          this.showWallProductModal = true;
          document.body.style.overflow = "hidden";
          this.$nextTick(() => this.$refs.unifiedProductModalClose?.focus());
        },

        closeUnifiedProductModal() {
          const opener = this.unifiedProductModalOpener;
          this.showWallProductModal = false;
          this.wallProductModalTarget = null;
          this.unifiedProductModalOpener = null;
          document.body.style.overflow = "";
          this.$nextTick(() => opener?.focus());
        },

        trapUnifiedProductModalFocus(event) {
          if (event.key !== "Tab") return;
          const dialog = event.currentTarget;
          const focusable = Array.from(
            dialog.querySelectorAll(
              'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
            ),
          );
          if (focusable.length === 0) return;
          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        },

        unifiedProductModalActiveId() {
          const target = this.wallProductModalTarget;
          if (target?.mode === "build") {
            return (
              this.settings.build_products?.[target.index]?.product_id || ""
            );
          }
          return this.settings.selected_product || "";
        },

        selectUnifiedProduct(productId) {
          const target = this.wallProductModalTarget;
          if (target?.mode === "build") {
            const row = this.settings.build_products?.[target.index];
            if (row) {
              row.product_id = productId;
              this.onBuildProductSelection(target.index);
            }
          } else {
            this.settings.selected_product = productId;
            this.onSingleProductSelection();
          }
          this.closeUnifiedProductModal();
        },

        productForUnifiedBuildRow(row) {
          return (
            this.combinedProducts.find(
              (product) => product.id === row?.product_id,
            ) || null
          );
        },

        onBuildProductSelection(index) {
          const row = this.settings.build_products?.[index];
          const product = this.productForUnifiedBuildRow(row);
          if (!row || !product) return;
          row.surface = product.surfaces[0];
          this.storeSettings();
        },

        addUnifiedBuildProduct() {
          this.settings.build_products.push({
            product_id: "",
            surface: "wall",
            quantity: null,
          });
        },

        removeUnifiedBuildProduct(index) {
          if (this.settings.build_products.length <= 1) return;
          const target = this.wallProductModalTarget;
          if (target?.mode === "build") {
            if (target.index === index) {
              this.closeUnifiedProductModal();
            } else if (target.index > index) {
              target.index -= 1;
            }
          }
          this.settings.build_products.splice(index, 1);
        },

        getSingleProductTierUrl(tierKey) {
          const product = this.selectedProduct;
          const tier =
            tierKey === "selection"
              ? this.singleProductSelection
              : this.singleProductTiers[tierKey];
          if (!product || !tier) return "#";
          const separator = product.url.includes("?") ? "&" : "?";
          return `${product.url}${separator}design_type=Felt Right Design&product_id=${product.id}&product_name=${encodeURIComponent(product.name)}&quantity=${tier.quantity}`;
        },

        normalizeBuildProducts() {
          this.settings.build_products =
            window.AcousticCalculatorProductTiers.normalizeBuildRows(
              this.settings,
              this.combinedProducts,
            );
          if (this.settings.build_products.length === 0) {
            this.settings.build_products.push({
              product_id: "",
              surface: "wall",
              quantity: null,
            });
          }
        },

        migrateSelectedProduct(wallMigrations, ceilingMigrations) {
          this.settings.selected_product = selectRestoredProductId(
            this.settings.selected_product,
            this.settings.wall_product,
            this.settings.ceiling_product,
            this.combinedProducts.map((product) => product.id),
            wallMigrations,
            ceilingMigrations,
          );
        },

        // The math below used to live here; it's now in the engine files
        // (acoustic-calculator-commercial-engine*.js). These Vue methods are
        // thin delegations that read this.settings / the commercial data
        // catalog and forward to the pure-function engine. Zero behavior
        // change vs. the pre-refactor implementation.

        assetUrl(filename) {
          return window.shopifyAssetBase
            ? window.shopifyAssetBase + filename
            : filename;
        },

        // Measure rendered label-callout widths (label + value box) and
        // bar container widths. Guarded assignment (only on change) so
        // calling from updated() cannot loop. Hidden elements measure 0
        // and are skipped.
        measureReverbLabelLayout() {
          // Vue 3 mounts this template as a fragment, so $el can be a text
          // node with no querySelector — fall back to document.
          var root =
            this.$el && typeof this.$el.querySelector === "function"
              ? this.$el
              : document;
          var bar = root.querySelector('[data-testid="reverb-bar"]');
          if (bar) {
            var bw = bar.getBoundingClientRect().width;
            if (bw > 0 && Math.abs(bw - this.reverbBarWidth) > 0.5) {
              this.reverbBarWidth = bw;
            }
          }
          var initialBar = root.querySelector(
            '[data-testid="initial-reverb-bar"]',
          );
          if (initialBar) {
            var ibw = initialBar.getBoundingClientRect().width;
            if (ibw > 0 && Math.abs(ibw - this.initialReverbBarWidth) > 0.5) {
              this.initialReverbBarWidth = ibw;
            }
          }
          var self = this;
          root
            .querySelectorAll("[data-marker-label-key]")
            .forEach(function (el) {
              var key = el.getAttribute("data-marker-label-key");
              var w = el.getBoundingClientRect().width;
              if (
                w > 0 &&
                Math.abs(w - (self.reverbLabelWidths[key] || 0)) > 0.5
              ) {
                self.reverbLabelWidths = Object.assign(
                  {},
                  self.reverbLabelWidths,
                  {
                    [key]: w,
                  },
                );
              }
            });
        },

        // Preserve the production marker treatment: triangles remain anchored
        // to their true values and nearby labels stagger vertically.
        mergeCoincidentReverbMarkers(markers) {
          var tierOrder = ["premium", "standard", "starter"];
          var tierNames = {
            premium: "Premium",
            standard: "Standard",
            starter: "Starter",
          };
          var groupsByValue = {};
          var output = [];

          markers.forEach(function (marker) {
            if (tierOrder.indexOf(marker.key) === -1) {
              output.push(Object.assign({}, marker));
              return;
            }

            var group = groupsByValue[marker.value];
            if (!group) {
              group = {
                markers: [],
                outputIndex: output.length,
              };
              groupsByValue[marker.value] = group;
              output.push(null);
            }
            group.markers.push(marker);
          });

          Object.keys(groupsByValue).forEach(function (value) {
            var group = groupsByValue[value];
            var sorted = group.markers.slice().sort(function (a, b) {
              return tierOrder.indexOf(a.key) - tierOrder.indexOf(b.key);
            });
            var pct =
              sorted.reduce(function (total, marker) {
                return total + marker.pct;
              }, 0) / sorted.length;
            var merged = Object.assign({}, sorted[0], { pct: pct });

            if (sorted.length > 1) {
              merged.key = sorted
                .map(function (marker) {
                  return marker.key;
                })
                .join("-");
              merged.label =
                sorted
                  .map(function (marker) {
                    return tierNames[marker.key];
                  })
                  .join(" + ") + "<br>Quantity";
            }

            output[group.outputIndex] = merged;
          });

          return output;
        },

        stackReverbMarkers(markers) {
          var MIN_GAP = 8;
          var STEP = 60;
          var stacked = markers
            .map(function (marker) {
              return Object.assign({}, marker);
            })
            .sort(function (a, b) {
              return a.pct - b.pct;
            });
          if (!stacked.length) return [];
          stacked[0].offset = 0;
          for (var i = 1; i < stacked.length; i++) {
            stacked[i].offset =
              stacked[i].pct - stacked[i - 1].pct < MIN_GAP
                ? stacked[i - 1].offset + STEP
                : 0;
          }
          return stacked;
        },

        // Retained for compatibility with local diagnostics that exercise the
        // collision engine directly; production bars use stackReverbMarkers.
        solveReverbMarkers(markers, barWidth) {
          var solved = window.AcousticCalculatorEngine.solveMarkerLabelRow(
            markers,
            this.reverbLabelWidths,
            barWidth,
            8,
          );
          return this.decorateSolvedMarkers(solved, markers, barWidth);
        },

        // Merge solver output with marker labels/values and compute the
        // connector geometry (straight stem vs staggered elbow). See
        // docs/superpowers/specs/2026-06-10-reverb-scroller-label-collision-design.md
        decorateSolvedMarkers(solved, sourceMarkers, containerPx) {
          var LABEL_ROW_TOP = 88;
          var TARGET_EXTRA = 24;
          var byKey = {};
          sourceMarkers.forEach(function (m) {
            byKey[m.key] = m;
          });
          return solved.map(function (m) {
            var desiredPx = (m.pct / 100) * containerPx;
            var off = m.labelCenterPx - desiredPx;
            var straight = Math.abs(off) <= 2;
            var horizTop = 44 + 12 * Math.min(m.clusterIndex, 3);
            var labelTop =
              m.key === "target" ? LABEL_ROW_TOP + TARGET_EXTRA : LABEL_ROW_TOP;
            return {
              key: m.key,
              pct: m.pct,
              label: byKey[m.key].label,
              value: byKey[m.key].value,
              straight: straight,
              labelOffsetPx: straight ? 0 : off,
              labelTopPx: labelTop,
              stemHeightPx: straight ? labelTop - 38 : horizTop - 38,
              horizTopPx: horizTop,
              horizLeftPx: Math.min(0, off),
              horizWidthPx: Math.abs(off),
              stubTopPx: horizTop,
              stubHeightPx: labelTop - horizTop,
            };
          });
        },

        clampDesiredRT60() {
          var min = this.reverbSliderMin;
          var max = this.reverbSliderMax;
          if (this.desiredRT60 < min) this.desiredRT60 = min;
          if (this.desiredRT60 > max) this.desiredRT60 = max;
        },

        createEmptySolution() {
          return window.AcousticCalculatorEngine.createEmptySolution();
        },

        finalizeSolution(solution) {
          return window.AcousticCalculatorEngine.finalizeSolution(
            solution,
            this.targetRT60,
            this._engineCtx,
          );
        },

        // Build solution query params shared by all URL generators
        buildSolutionParams() {
          let params = "";

          if (this.solutions.wallsFirst) {
            params += `&max_wall_quantity=${this.solutions.wallsFirst.wallQuantity || 0}`;
            params += `&max_wall_sqft=${this.solutions.wallsFirst.wallSqFt || 0}`;
            params += `&max_wall_ceiling_quantity=${this.solutions.wallsFirst.ceilingQuantity || 0}`;
            params += `&max_wall_ceiling_sqft=${this.solutions.wallsFirst.ceilingSqFt || 0}`;
          }

          if (this.solutions.balanced) {
            params += `&balanced_wall_quantity=${this.solutions.balanced.wallQuantity || 0}`;
            params += `&balanced_wall_sqft=${this.solutions.balanced.wallSqFt || 0}`;
            params += `&balanced_ceiling_quantity=${this.solutions.balanced.ceilingQuantity || 0}`;
            params += `&balanced_ceiling_sqft=${this.solutions.balanced.ceilingSqFt || 0}`;
          }

          if (this.solutions.ceilingFirst) {
            params += `&max_ceiling_wall_quantity=${this.solutions.ceilingFirst.wallQuantity || 0}`;
            params += `&max_ceiling_wall_sqft=${this.solutions.ceilingFirst.wallSqFt || 0}`;
            params += `&max_ceiling_quantity=${this.solutions.ceilingFirst.ceilingQuantity || 0}`;
            params += `&max_ceiling_sqft=${this.solutions.ceilingFirst.ceilingSqFt || 0}`;
          }

          return params;
        },

        // ========== Core RT60 Calculation Methods (engine delegations) ==========

        calculateRT60(volume, totalAbsorption) {
          return window.AcousticCalculatorEngine.calculateRT60(
            volume,
            totalAbsorption,
          );
        },

        calculateRequiredAbsorption(volume, targetRT60) {
          return window.AcousticCalculatorEngine.calculateRequiredAbsorption(
            volume,
            targetRT60,
          );
        },

        calculateExistingAbsorption(frequency) {
          return window.AcousticCalculatorEngine.calculateExistingAbsorption(
            frequency,
            this._engineCtx,
          );
        },

        calculateAllFrequencies() {
          const results = {};
          const eng = window.AcousticCalculatorEngine;
          const ctx = this._engineCtx;
          const target = this.targetRT60;
          this.frequencyBands.forEach((frequency) => {
            const existing = eng.calculateExistingAbsorption(frequency, ctx);
            const required = eng.calculateRequiredAbsorption(
              ctx.roomVolume,
              target,
            );
            const currentRT60 = eng.calculateRT60(ctx.roomVolume, existing);
            results[frequency] = {
              existingAbsorption: existing,
              requiredAbsorption: required,
              additionalNeeded: Math.max(0, required - existing),
              currentRT60: currentRT60,
            };
          });
          return results;
        },

        countClosedBands(rt60Values, targetRT60) {
          return window.AcousticCalculatorEngine.countClosedBands(
            rt60Values,
            targetRT60,
          );
        },

        // ========== Solution Calculation Methods (engine delegations) ==========

        calculateProductQuantity(requiredSabins, productAbsorption) {
          return window.AcousticCalculatorEngine.calculateProductQuantity(
            requiredSabins,
            productAbsorption,
          );
        },

        calculateRT60WithProducts(frequency, wallQuantity, ceilingQuantity) {
          return window.AcousticCalculatorEngine.calculateRT60WithProducts(
            frequency,
            wallQuantity,
            ceilingQuantity,
            this._engineCtx,
          );
        },

        findQuantityToCloseBand(frequency, productType, targetRT60) {
          return window.AcousticCalculatorEngine.findQuantityToCloseBand(
            frequency,
            productType,
            targetRT60,
            this._engineCtx,
          );
        },

        // The 3 solution builders delegate to the engine. The orchestrator
        // calculateSolutions() still calls these in order so balanced can
        // read this.solutions.wallsFirst / ceilingFirst set just before.
        calculateWallsFirst() {
          return window.AcousticCalculatorEngine.calculateWallsFirst(
            this.desiredRT60,
            this._engineCtx,
          );
        },

        calculateBalanced() {
          return window.AcousticCalculatorEngine.calculateBalanced(
            this.desiredRT60,
            this.solutions.wallsFirst,
            this.solutions.ceilingFirst,
            this._engineCtx,
          );
        },

        calculateCeilingFirst() {
          return window.AcousticCalculatorEngine.calculateCeilingFirst(
            this.desiredRT60,
            this._engineCtx,
          );
        },

        // ========== Enhanced User Experience Methods ==========

        // Validation method
        validateInputs() {
          const errors = [];

          // Room dimension validation
          if (
            !this.settings.room_length ||
            this.settings.room_length < 1 ||
            this.settings.room_length > 500
          ) {
            errors.push("Room length must be between 1 and 500 feet");
          }
          if (
            !this.settings.room_width ||
            this.settings.room_width < 1 ||
            this.settings.room_width > 500
          ) {
            errors.push("Room width must be between 1 and 500 feet");
          }
          if (
            !this.settings.room_height ||
            this.settings.room_height < 1 ||
            this.settings.room_height > 50
          ) {
            errors.push("Room height must be between 1 and 50 feet");
          }

          // Building/room validation
          if (!this.settings.building) {
            errors.push("Please select a building type");
          }
          if (!this.settings.room) {
            errors.push("Please select a room type");
          }

          // Material validation
          const walls = Array.isArray(this.settings.walls)
            ? this.settings.walls
            : [];
          const missingWallIndices = walls
            .map((w, i) => (w?.material_id ? null : i + 1))
            .filter((i) => i !== null);
          if (walls.length < 4 || missingWallIndices.length > 0) {
            errors.push(
              missingWallIndices.length === walls.length
                ? "Please select a material for each wall"
                : `Please select a material for wall ${missingWallIndices.join(", ")}`,
            );
          }
          if (!this.settings.ceiling_material) {
            errors.push("Please select a ceiling material");
          }
          if (!this.settings.floor_material) {
            errors.push("Please select a floor material");
          }

          // Product validation
          if (!this.settings.wall_product) {
            errors.push("Please select a wall product");
          }
          if (!this.settings.ceiling_product) {
            errors.push("Please select a ceiling product");
          }

          this.validationErrors = errors;
          return errors.length === 0;
        },

        // Debounced calculation method
        debouncedCalculate() {
          // Clear existing timer
          if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
          }

          // Set new timer
          this.debounceTimer = setTimeout(() => {
            this.calculateSolutions();
          }, 500); // 500ms delay
        },

        // Format number with commas
        formatNumber(num) {
          if (!num && num !== 0) return "0";
          return Math.round(num).toLocaleString();
        },

        // Format RT60 value with 4 decimal places
        formatRT60(value) {
          if (!value && value !== 0) return "0.0000s";
          return parseFloat(value).toFixed(4) + "s";
        },

        // Main calculation method
        calculateSolutions() {
          // Validate inputs first
          if (!this.validateInputs()) {
            this.isCalculating = false;
            return;
          }

          this.isCalculating = true;

          // Use setTimeout to make calculation async for better UX
          setTimeout(() => {
            try {
              // Calculate existing absorption for all frequencies
              this.existingAbsorptionByFrequency = {};
              this.requiredAbsorptionByFrequency = {};

              // Calculate existing absorption for all frequencies
              this.frequencyBands.forEach((frequency) => {
                const existing = this.calculateExistingAbsorption(frequency);
                const required = this.calculateRequiredAbsorption(
                  this.roomVolume,
                  this.targetRT60,
                );
                this.existingAbsorptionByFrequency[frequency] = existing;
                this.requiredAbsorptionByFrequency[frequency] = required;
              });

              // Calculate full recommended solutions at targetRT60, then
              // scale by slider fraction. Sheet rows 36-38 are the 100%
              // quantities; rows 46-48 = ROUNDDOWN(row * A42).
              var eng = window.AcousticCalculatorEngine;
              var target = this.effectiveTargetRT60;
              var recWF = eng.calculateWallsFirst(target, this._engineCtx);
              var recCF = eng.calculateCeilingFirst(target, this._engineCtx);
              var recBal = eng.calculateBalanced(
                target,
                recWF,
                recCF,
                this._engineCtx,
              );
              this.recommendedSolutions = {
                wallsFirst: recWF,
                balanced: recBal,
                ceilingFirst: recCF,
              };

              // Find the quantity fraction (0–1) that produces the desired
              // RT60. Binary search because Sabine (RT60 = k/A) is non-linear.
              var recArr = [recWF, recBal, recCF];
              var fracBands = ["4000 Hz", "2000 Hz", "1000 Hz", "500 Hz"];
              function avgMaxRT60AtFraction(f) {
                var vals = [];
                for (var si = 0; si < recArr.length; si++) {
                  var sol = recArr[si];
                  if (!sol) continue;
                  var wQ = Math.floor((sol.wallQuantity || 0) * f);
                  var cQ = Math.floor((sol.ceilingQuantity || 0) * f);
                  var mx = 0;
                  for (var bi = 0; bi < fracBands.length; bi++) {
                    var r = eng.calculateRT60WithProducts(
                      fracBands[bi],
                      wQ,
                      cQ,
                      ctx,
                    );
                    if (r > mx) mx = r;
                  }
                  if (mx > 0) vals.push(mx);
                }
                if (vals.length === 0) return 0;
                var s = 0;
                for (var vi = 0; vi < vals.length; vi++) s += vals[vi];
                return s / vals.length;
              }
              var ctx = this._engineCtx;
              var fraction;
              if (this.desiredRT60 >= this.currentRT60) {
                fraction = 0;
              } else if (this.desiredRT60 <= target) {
                fraction = 1;
              } else {
                var lo = 0,
                  hi = 1;
                for (var it = 0; it < 20; it++) {
                  var mid = (lo + hi) / 2;
                  var rt = avgMaxRT60AtFraction(mid);
                  if (rt > this.desiredRT60) lo = mid;
                  else hi = mid;
                }
                fraction = (lo + hi) / 2;
              }

              var self = this;
              function scaleSolution(rec) {
                var wallQ = Math.floor((rec.wallQuantity || 0) * fraction);
                var ceilQ = Math.floor((rec.ceilingQuantity || 0) * fraction);
                var scaled = {
                  wallQuantity: wallQ,
                  ceilingQuantity: ceilQ,
                  wallSqFt:
                    wallQ *
                    (self._engineCtx.wallProduct
                      ? self._engineCtx.wallProduct.footprint
                      : 0),
                  ceilingSqFt:
                    ceilQ *
                    (self._engineCtx.ceilingProduct
                      ? self._engineCtx.ceilingProduct.footprint
                      : 0),
                  wallNeeded: rec.wallNeeded || 0,
                  ceilingNeeded: rec.ceilingNeeded || 0,
                  rt60ByFrequency: {},
                  bandsClosedCount: 0,
                  bandsClosed: [false, false, false, false, false],
                  meetsTarget: false,
                  showContactSales: false,
                };
                return eng.finalizeSolution(scaled, target, self._engineCtx);
              }

              this.solutions.wallsFirst = scaleSolution(recWF);
              this.solutions.balanced = scaleSolution(recBal);
              this.solutions.ceilingFirst = scaleSolution(recCF);

              // Sheet B36: =IF(AND(B88=0, B110=0), "Contact Sales", B89)
              // Contact Sales triggers when all sizing gives zero, NOT from a
              // volume cap. Large rooms (>50K ft³) still get valid solutions.

              // Check if all recommended solutions have zero quantities
              var allSolutionsZero =
                recWF.wallQuantity === 0 &&
                recWF.ceilingQuantity === 0 &&
                recBal.wallQuantity === 0 &&
                recBal.ceilingQuantity === 0 &&
                recCF.wallQuantity === 0 &&
                recCF.ceilingQuantity === 0;

              var Engine = window.AcousticCalculatorEngine;

              var targetAlreadyMet =
                Engine &&
                Engine.detectContactSalesReason &&
                Engine.detectContactSalesReason(
                  this._engineCtx,
                  this.targetRT60,
                ) === "target_already_met";

              var desiredMeetsExisting = this.desiredRT60 >= this.currentRT60;
              if (
                (allSolutionsZero && !desiredMeetsExisting) ||
                targetAlreadyMet
              ) {
                this.solutions.wallsFirst.showContactSales = true;
                this.solutions.balanced.showContactSales = true;
                this.solutions.ceilingFirst.showContactSales = true;
              }

              // Add explanation based on RT60 at 250Hz
              // Formula from Excel: =IF(OR(G79>3, G80>3, G95>3),"",(IF($H$66=0, $A$105, A104)))
              // Check if any solution has RT60 at 250Hz > 3
              const wallsFirstRT60_250Hz = this.solutions.wallsFirst
                .rt60ByFrequency
                ? this.solutions.wallsFirst.rt60ByFrequency["250 Hz"] || 0
                : 0;
              const balancedRT60_250Hz = this.solutions.balanced.rt60ByFrequency
                ? this.solutions.balanced.rt60ByFrequency["250 Hz"] || 0
                : 0;
              const ceilingFirstRT60_250Hz = this.solutions.ceilingFirst
                .rt60ByFrequency
                ? this.solutions.ceilingFirst.rt60ByFrequency["250 Hz"] || 0
                : 0;

              if (
                wallsFirstRT60_250Hz <= 3 &&
                balancedRT60_250Hz <= 3 &&
                ceilingFirstRT60_250Hz <= 3
              ) {
                // If none exceed 3 seconds at 250Hz, show explanation
                // Using the standard explanation (equivalent to A105 in Excel)
                this.solutionExplanation =
                  window.AcousticCalculatorCommercialData.explanations.standard;
              } else {
                // If any exceed 3 seconds, show no explanation
                this.solutionExplanation = "";
              }

              // Complete progress
              this.calculationProgress = 100;

              // Store settings for persistence (if the method exists)
              if (
                this.storeSettings &&
                typeof this.storeSettings === "function"
              ) {
                this.storeSettings();
              }
            } catch (error) {
              this.validationErrors = [
                "An error occurred during calculation. Please check your inputs.",
              ];
            }

            this.isCalculating = false;
          }, 200); // Small delay for better UX
        },

        // ========== UI Methods ==========

        // When building changes, snap room to the first option for that building
        // so the (building, room) pair stays valid for computeTargetRT60.
        onBuildingChange() {
          const rooms = this.roomsForBuilding;
          if (!rooms.includes(this.settings.room)) {
            this.settings.room = rooms[0] || "";
          }
          this.storeSettings();
          this.calculateSolutions();
        },

        onRoomChange() {
          this.storeSettings();
          this.calculateSolutions();
        },

        productForBuildRow(surface, row) {
          const list =
            surface === "ceiling"
              ? this.feltRightProducts.ceiling
              : this.feltRightProducts.wall;
          return list.find((product) => product.id === row.product_id) || null;
        },

        ensureBuildProductRows() {
          if (!Array.isArray(this.settings.build_wall_products)) {
            this.settings.build_wall_products = [];
          }
          if (!Array.isArray(this.settings.build_ceiling_products)) {
            this.settings.build_ceiling_products = [];
          }

          if (this.settings.build_wall_products.length === 0) {
            this.settings.build_wall_products.push({
              product_id:
                this.settings.wall_product ||
                this.feltRightProducts.wall[0]?.id ||
                "",
              quantity: 0,
            });
          }
          if (this.settings.build_ceiling_products.length === 0) {
            this.settings.build_ceiling_products.push({
              product_id:
                this.settings.ceiling_product ||
                this.feltRightProducts.ceiling[0]?.id ||
                "",
              quantity: 0,
            });
          }
        },

        addBuildProductRow(surface) {
          this.ensureBuildProductRows();
          const key =
            surface === "ceiling"
              ? "build_ceiling_products"
              : "build_wall_products";
          const fallback =
            surface === "ceiling"
              ? this.settings.ceiling_product ||
                this.feltRightProducts.ceiling[0]?.id ||
                ""
              : this.settings.wall_product ||
                this.feltRightProducts.wall[0]?.id ||
                "";

          this.settings[key].push({ product_id: fallback, quantity: 0 });
        },

        removeBuildProductRow(surface, index) {
          const key =
            surface === "ceiling"
              ? "build_ceiling_products"
              : "build_wall_products";
          if (
            !Array.isArray(this.settings[key]) ||
            this.settings[key].length < 2
          ) {
            return;
          }
          this.settings[key].splice(index, 1);
        },

        syncBuildProductSelection(surface, index) {
          const key =
            surface === "ceiling"
              ? "build_ceiling_products"
              : "build_wall_products";
          const row = this.settings[key]?.[index];
          if (!row || index !== 0) return;
          if (surface === "ceiling") {
            this.settings.ceiling_product = row.product_id;
          } else {
            this.settings.wall_product = row.product_id;
          }
        },

        calculateBuildModeRT60(frequency, fraction) {
          const tierFraction = Number.isFinite(fraction) ? fraction : 1;
          const rows = this.buildModeNormalizedRows.map((row) => ({
            ...row,
            quantity: Math.ceil((Number(row.quantity) || 0) * tierFraction),
          }));
          return this.calculateBuildMixRT60(frequency, rows);
        },

        calculateBuildMixRT60(frequency, rows) {
          const Engine = window.AcousticCalculatorEngine;
          const ctx = this._engineCtx;
          let absorption = Engine.calculateExistingAbsorption(frequency, ctx);
          const productsById = this.combinedProducts.reduce((byId, product) => {
            byId[product.id] = product;
            return byId;
          }, {});

          (rows || []).forEach((row) => {
            const qty = Math.ceil(Number(row.quantity) || 0);
            const product = productsById[row.productId || row.product_id];
            if (qty > 0 && product) {
              absorption += qty * (product.absorption?.[frequency] || 0);
            }
          });

          return Engine.calculateRT60(ctx.roomVolume, absorption);
        },

        // Scroll to contact form
        scrollToContactForm(e) {
          if (e) e.preventDefault();
          const form = document.getElementById("contact-form");
          if (form) {
            form.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        },

        openReportModal() {
          this.reportModalOpen = true;
          document.body.style.overflow = "hidden";
        },

        closeReportModal() {
          this.reportModalOpen = false;
          document.body.style.overflow = "";
        },

        async submitReport() {
          if (this.reportSubmitting) return;
          if (!this.reportForm.name || !this.reportForm.email) {
            Swal.fire({
              icon: "warning",
              text: "Please fill in all required fields (Name and Email).",
            });
            return;
          }
          this.reportSubmitting = true;
          try {
            var contact = {
              name: this.reportForm.name,
              email: this.reportForm.email,
              phone: this.reportForm.phone || "",
              company: this.reportForm.company || "",
              project_type:
                this.settings.building === "Residential"
                  ? "Residential"
                  : "Commercial",
              email_opt_in: this.reportForm.emailOptIn,
            };
            var reportRequest =
              await window.AcousticsReportPayload.createAcousticsReportRequest(
                this,
                { contact: contact },
              );
            this.closeReportModal();
            Swal.fire({
              icon: "success",
              text:
                reportRequest.email_sent === false
                  ? "We received your request. If the report doesn't arrive in your inbox shortly, contact support@feltright.com."
                  : "Your report has been sent! Check your email for the full acoustical report.",
            });
            this.reportForm = {
              name: "",
              email: "",
              phone: "",
              company: "",
              emailOptIn: false,
            };
          } catch (error) {
            console.error("Report submission failed:", error);
            Swal.fire({
              icon: "error",
              text: "Something went wrong. Please try again.",
            });
          } finally {
            this.reportSubmitting = false;
          }
        },

        // Get recommendation quality
        getRecommendationQuality(solution) {
          if (solution.bandsClosedCount >= 5) return "Excellent";
          if (solution.bandsClosedCount >= 4) return "Good";
          if (solution.bandsClosedCount >= 3) return "Fair";
          return "Poor";
        },

        // Set active solution tab
        setActiveSolution(solutionType) {
          this.activeSolutionTab = solutionType;
        },

        // ========== Settings Management ==========

        saveSettings() {
          try {
            const settings = JSON.stringify(this.settings);
            localStorage.setItem("acousticCalculatorSettings", settings);
          } catch (e) {
            // Safari Private Browsing / ITP throws QuotaExceededError
            console.warn("Could not save acoustic calculator settings:", e);
          }
        },

        updateUrlHash() {
          if (this._hashTimer) clearTimeout(this._hashTimer);
          this._hashTimer = setTimeout(() => {
            var Engine = window.AcousticCalculatorEngine;
            if (!Engine || !Engine.hashInputs) return;
            var id = Engine.hashInputs(this.settings);
            this.calcId = id;
            var json = JSON.stringify(this.settings);
            var encoded = btoa(unescape(encodeURIComponent(json)));
            history.replaceState(null, "", "#calc=" + id + "&state=" + encoded);
          }, 300);
        },

        loadFromUrlHash() {
          var hash = window.location.hash;
          if (!hash || !hash.includes("state=")) return false;
          var match = hash.match(/state=([A-Za-z0-9+/=]+)/);
          if (!match) return false;
          try {
            var json = decodeURIComponent(escape(atob(match[1])));
            var parsed = JSON.parse(json);
            this.settings = { ...this.settings, ...parsed };
            if (
              !Object.prototype.hasOwnProperty.call(parsed, "selected_product")
            ) {
              delete this.settings.selected_product;
            }
            if (!Array.isArray(parsed.build_products)) {
              delete this.settings.build_products;
            }
            this.settings = migrateLegacyProductSettings(
              this.settings,
              WALL_PRODUCT_MIGRATION,
              CEILING_PRODUCT_MIGRATION,
            );
            this.normalizeBuildProducts();
            this.migrateSelectedProduct(
              WALL_PRODUCT_MIGRATION,
              CEILING_PRODUCT_MIGRATION,
            );
            this.syncSelectedProductSettings();
            var idMatch = hash.match(/calc=([0-9a-f]{6})/);
            if (idMatch) this.calcId = idMatch[1];
            return true;
          } catch (e) {
            return false;
          }
        },

        loadCalcById() {
          this.loadCalcError = "";
          var input = (this.loadCalcInput || "").trim();
          if (!input) {
            this.loadCalcError = "Please enter a calculation ID or URL.";
            return;
          }
          var hash = "";
          if (input.includes("#calc=")) {
            hash = input.substring(input.indexOf("#"));
          } else if (/^[0-9a-f]{6}$/i.test(input)) {
            hash = window.location.hash;
            if (!hash.includes("calc=" + input.toLowerCase())) {
              this.loadCalcError =
                "Calculation not found. Paste the full URL or use a valid ID from the address bar.";
              return;
            }
          } else {
            this.loadCalcError =
              "Invalid format. Paste a calculator URL or 6-character ID.";
            return;
          }
          var stateMatch = hash.match(/state=([A-Za-z0-9+/=]+)/);
          if (!stateMatch) {
            this.loadCalcError =
              "Calculation not found. Paste the full URL or use a valid ID from the address bar.";
            return;
          }
          try {
            var json = decodeURIComponent(escape(atob(stateMatch[1])));
            var parsed = JSON.parse(json);
            this.settings = { ...this.settings, ...parsed };
            if (
              !Object.prototype.hasOwnProperty.call(parsed, "selected_product")
            ) {
              delete this.settings.selected_product;
            }
            if (!Array.isArray(parsed.build_products)) {
              delete this.settings.build_products;
            }
            this.settings = migrateLegacyProductSettings(
              this.settings,
              WALL_PRODUCT_MIGRATION,
              CEILING_PRODUCT_MIGRATION,
            );
            this.normalizeBuildProducts();
            this.migrateSelectedProduct(
              WALL_PRODUCT_MIGRATION,
              CEILING_PRODUCT_MIGRATION,
            );
            this.syncSelectedProductSettings();
            var idMatch = hash.match(/calc=([0-9a-f]{6})/);
            if (idMatch) this.calcId = idMatch[1];
            this.loadCalcInput = "";
            this.calculateSolutions();
          } catch (e) {
            this.loadCalcError =
              "Invalid calculation data. The link may be corrupted.";
          }
        },

        applyPreset(preset) {
          var keys = [
            "building",
            "room",
            "room_length",
            "room_width",
            "room_height",
            "ceiling_material",
            "floor_material",
            "open_entryways",
            "available_wall_space",
            "available_ceiling_space",
            "furniture",
          ];
          keys.forEach((key) => {
            if (preset[key] !== undefined) {
              this.$set(this.settings, key, preset[key]);
            }
          });
          if (Array.isArray(preset.walls)) {
            this.settings.walls.splice(
              0,
              this.settings.walls.length,
              ...preset.walls,
            );
          }
          var dividerQuantity = Number(preset.room_divider_quantity) || 0;
          this.$set(
            this.settings,
            "room_dividers_enabled",
            dividerQuantity > 0,
          );
          this.$set(this.settings, "room_divider_quantity", dividerQuantity);
          this.calculateSolutions();
        },

        loadSettings() {
          var saved;
          try {
            saved = localStorage.getItem("acousticCalculatorSettings");
          } catch (e) {
            // Safari Private Browsing makes localStorage unavailable
            console.warn("localStorage not available:", e);
            return;
          }

          if (saved) {
            try {
              const parsedSettings = JSON.parse(saved);
              this.settings = { ...this.settings, ...parsedSettings };
              if (
                !Object.prototype.hasOwnProperty.call(
                  parsedSettings,
                  "selected_product",
                )
              ) {
                delete this.settings.selected_product;
              }
              if (!Array.isArray(parsedSettings.build_products)) {
                delete this.settings.build_products;
              }
              this.settings = migrateLegacyProductSettings(
                this.settings,
                WALL_PRODUCT_MIGRATION,
                CEILING_PRODUCT_MIGRATION,
              );
              this.settings.surface_type = "commercial";

              // Migrate legacy material IDs to current sheet-aligned IDs.
              const WALL_ID_MIGRATION = {
                gypsum: "gypsum_plaster",
                cmu: "concrete_block",
                concrete: "solid_concrete",
              };

              // Drop legacy single wall_material key.
              delete this.settings.wall_material;
              // Remap any legacy IDs still sitting in the new walls[] array.
              if (Array.isArray(this.settings.walls)) {
                this.settings.walls = this.settings.walls.map((w) => {
                  const id = w?.material_id || "";
                  return { material_id: WALL_ID_MIGRATION[id] || id };
                });
              }

              const FLOOR_ID_MIGRATION = {
                wood: "natural_wood",
              };
              if (FLOOR_ID_MIGRATION[this.settings.floor_material]) {
                this.settings.floor_material =
                  FLOOR_ID_MIGRATION[this.settings.floor_material];
              }

              const CEILING_ID_MIGRATION = {
                gypsum: "gypsum_plaster",
                wood: "natural_wood",
                metal: "metal_deck",
                act: "suspended_ceiling",
              };
              if (CEILING_ID_MIGRATION[this.settings.ceiling_material]) {
                this.settings.ceiling_material =
                  CEILING_ID_MIGRATION[this.settings.ceiling_material];
              }

              // Migrate legacy carpeting_percentage → floor_covering_id/pct.
              const hasLegacyCarpeting =
                parsedSettings &&
                ("carpeting_percentage" in parsedSettings ||
                  "carpet_type" in parsedSettings);
              const hasNewCovering =
                parsedSettings &&
                ("floor_covering_id" in parsedSettings ||
                  "floor_covering_pct" in parsedSettings);
              if (hasLegacyCarpeting && !hasNewCovering) {
                const legacyPct = Number(parsedSettings.carpeting_percentage);
                const pct = Number.isFinite(legacyPct) ? legacyPct : 0;
                this.settings.floor_covering_pct = pct;
                this.settings.floor_covering_id =
                  pct > 0 ? "light_carpet" : "none";
              }
              delete this.settings.carpeting_percentage;
              delete this.settings.carpet_type;

              // Ensure building/room pair is valid in current catalog.
              this.migrateBuildingRoom();
              this.normalizeBuildProducts();
              this.migrateSelectedProduct(
                WALL_PRODUCT_MIGRATION,
                CEILING_PRODUCT_MIGRATION,
              );
              this.syncSelectedProductSettings();

              this.calculateSolutions();
            } catch (e) {
              this.migrateBuildingRoom();
            }
          } else {
            this.migrateBuildingRoom();
          }
        },

        // Ensure settings.building/room are valid per current ROOM_TYPES.
        // Drops legacy selectedRoomTypes array if it exists.
        migrateBuildingRoom() {
          if ("selectedRoomTypes" in this.settings) {
            delete this.settings.selectedRoomTypes;
          }
          const defaults = commercialData.defaultBuildingRoom || {
            building: "Business/Corporate",
            room: "Open Office",
          };
          const rooms = commercialData.ROOM_TYPES || [];
          const valid = rooms.some(
            (r) =>
              r.building === this.settings.building &&
              r.room === this.settings.room,
          );
          if (!valid) {
            this.settings.building = defaults.building;
            this.settings.room = defaults.room;
          }
          this.ensureBuildProductRows();
        },

        resetSettings() {
          this.settings = { ...this.defaultSettings };
          this.calculateSolutions();
        },

        // ========== Display Helpers ==========

        productDisplayName(product) {
          if (!product) return "";
          if (
            product.name.toLowerCase().includes("set") ||
            product.name.toLowerCase().includes("pack")
          )
            return product.name;
          return "Individual " + product.name;
        },

        // ========== Product URL Generation ==========

        getWallProductUrl(solution) {
          const product = this.selectedWallProduct;
          if (!product || !solution) return "#";

          // Use & if URL already has query params (e.g. ?variant=), otherwise use ?
          const separator = product.url.includes("?") ? "&" : "?";
          let url = `${product.url}${separator}design_type=Felt Right Design`;
          url += `&product_id=${product.id}`;
          url += `&product_name=${encodeURIComponent(product.name)}`;
          url += this.buildSolutionParams();

          if (this.selectedCeilingProduct) {
            url += `&ceiling_product_id=${this.selectedCeilingProduct.id}`;
            url += `&ceiling_product_url=${encodeURIComponent(this.selectedCeilingProduct.url)}`;
          }

          return url;
        },

        getCeilingProductUrl(solution) {
          const product = this.selectedCeilingProduct;
          if (!product || !solution) return "#";

          // Use & if URL already has query params (e.g. ?variant=), otherwise use ?
          const separator = product.url.includes("?") ? "&" : "?";
          let url = `${product.url}${separator}design_type=Felt Right Design`;
          url += `&product_id=${product.id}`;
          url += `&product_name=${encodeURIComponent(product.name)}`;
          url += this.buildSolutionParams();

          // Include wall product info so the ceiling PDP can link back to the wall product
          if (this.selectedWallProduct) {
            url += `&wall_product_id=${this.selectedWallProduct.id}`;
            url += `&wall_product_url=${encodeURIComponent(this.selectedWallProduct.url)}`;
          }

          if (this.selectedCeilingProduct) {
            url += `&ceiling_product_id=${this.selectedCeilingProduct.id}`;
          }

          return url;
        },

        getCombinedProductUrl(solution) {
          if (!solution) return "#";

          let url = "/pages/design-gallery?design_type=Felt Right Design";

          if (this.selectedWallProduct) {
            url += `&wall_product_id=${this.selectedWallProduct.id}`;
            url += `&wall_product_name=${encodeURIComponent(this.selectedWallProduct.name)}`;
          }

          if (this.selectedCeilingProduct) {
            url += `&ceiling_product_id=${this.selectedCeilingProduct.id}`;
            url += `&ceiling_product_name=${encodeURIComponent(this.selectedCeilingProduct.name)}`;
          }

          url += this.buildSolutionParams();

          return url;
        },

        getDesignGalleryUrl() {
          // Generate URL for design gallery with commercial calculator parameters

          // Determine which solution to use (balanced is recommended)
          let solution =
            this.solutions.balanced ||
            this.solutions.wallsFirst ||
            this.solutions.ceilingFirst;
          if (!solution) return "#";

          const wallProduct = this.selectedWallProduct;
          const ceilingProduct = this.selectedCeilingProduct;

          // Base URL for design gallery
          let url = "/pages/design-gallery?";

          // Identify this as coming from commercial calculator
          url += "calculatorType=commercial";

          // Add wall product info
          if (wallProduct) {
            url += `&wallProduct=${wallProduct.id}`;
            url += `&wallQuantity=${solution.wallQuantity || 0}`;
            url += `&wallSqFt=${solution.wallSqFt || 0}`;
          }

          // Add ceiling product info
          if (ceilingProduct) {
            url += `&ceilingProduct=${ceilingProduct.id}`;
            url += `&ceilingQuantity=${solution.ceilingQuantity || 0}`;
            url += `&ceilingSqFt=${solution.ceilingSqFt || 0}`;
          }

          // Add which solution type they're viewing
          let solutionType = "balanced"; // Default to balanced
          if (this.availableCeilingSqFt === 0) {
            solutionType = "wallsOnly";
          }
          url += `&solutionType=${solutionType}`;

          // Add building + room for PDP context
          if (this.settings.building) {
            url += `&building=${encodeURIComponent(this.settings.building)}`;
          }
          if (this.settings.room) {
            url += `&room=${encodeURIComponent(this.settings.room)}`;
          }

          return url;
        },

        calculateSuperUserRT60() {
          if (!this.showSuperUser) return;
          const Engine = window.AcousticCalculatorEngine;
          if (!Engine || !Engine.buildContext) return;

          const catalog = {
            wallMaterials: this.wallMaterials,
            ceilingMaterials: this.ceilingMaterials,
            floorMaterials: this.floorMaterials,
            feltRightProducts: {
              wall: this.feltRightProducts.wall,
              ceiling: this.feltRightProducts.ceiling,
            },
            furnitureOptions: this.furnitureOptions,
            floorCoverings: this.floorCoverings,
          };

          const ctx = Engine.buildContext(this.settings, catalog);
          const bands = Engine.BANDS;
          const rt60ByBand = {};
          let sum = 0;

          bands.forEach((freq) => {
            let absorption = Engine.calculateExistingAbsorption(freq, ctx);
            this.superUserProducts.forEach((p) => {
              if (p.qty > 0 && p.absorption[freq]) {
                absorption += p.qty * p.absorption[freq];
              }
            });
            const rt60 = Engine.calculateRT60(ctx.roomVolume, absorption);
            rt60ByBand[freq] = rt60;
            sum += rt60;
          });

          this.superUserRT60 = rt60ByBand;
          this.superUserAvgRT60 = bands.length > 0 ? sum / bands.length : 0;
        },

        resetSuperUserQuantities() {
          this.superUserProducts.forEach((p) => {
            p.qty = 0;
          });
        },

        teardownCommercialCalculator() {
          if (this._documentKeydownHandler) {
            document.removeEventListener(
              "keydown",
              this._documentKeydownHandler,
            );
            this._documentKeydownHandler = null;
          }
          if (this._reverbResizeHandler) {
            window.removeEventListener("resize", this._reverbResizeHandler);
            this._reverbResizeHandler = null;
          }
          clearTimeout(this._reverbResizeTimer);
          clearTimeout(this._hashTimer);
          clearTimeout(this.debounceTimer);
          this._reverbResizeTimer = null;
          this._hashTimer = null;
          this.debounceTimer = null;
          this.showWallProductModal = false;
          this.wallProductModalTarget = null;
          this.unifiedProductModalOpener = null;
          document.body.style.overflow = "";
        },
      },

      mounted() {
        // URL hash takes priority over localStorage
        var restoredFromHash = this.loadFromUrlHash();
        if (!restoredFromHash) {
          this.loadSettings();
        }
        this.normalizeBuildProducts();
        this.migrateSelectedProduct(
          WALL_PRODUCT_MIGRATION,
          CEILING_PRODUCT_MIGRATION,
        );
        this.syncSelectedProductSettings();

        this._documentKeydownHandler = (e) => {
          if (e.key === "Escape") {
            if (this.showWallProductModal) this.closeUnifiedProductModal();
          }
        };
        document.addEventListener("keydown", this._documentKeydownHandler);

        // Evaluate super-user gate
        const sectionSettings = window.acousticSectionSettings || {};
        this.showSuperUser = computeShowSuperUser(sectionSettings);

        // Check if the selected wall product is still available after filtering
        // If not, reset to the first available product
        if (this.settings.wall_product) {
          const wallProductStillAvailable = this.feltRightProducts.wall.some(
            (p) => p.id === this.settings.wall_product,
          );
          if (
            !wallProductStillAvailable &&
            this.feltRightProducts.wall.length > 0
          ) {
            // Reset to first available product
            this.settings.wall_product = this.feltRightProducts.wall[0].id;
          }
        }
        this.ensureBuildProductRows();
        this.settings.build_wall_products =
          this.settings.build_wall_products.map((row) => ({
            product_id: this.feltRightProducts.wall.some(
              (product) => product.id === row.product_id,
            )
              ? row.product_id
              : this.settings.wall_product,
            quantity: Number(row.quantity) || 0,
          }));
        this.settings.build_ceiling_products =
          this.settings.build_ceiling_products.map((row) => ({
            product_id: this.feltRightProducts.ceiling.some(
              (product) => product.id === row.product_id,
            )
              ? row.product_id
              : this.settings.ceiling_product,
            quantity: Number(row.quantity) || 0,
          }));

        // Seed the reverb-bar thumb at Target (targetRT60), clamped to the
        // fixed 0–2.4+ visual scale.
        this.desiredRT60 = Math.min(
          this.targetRT60,
          window.AcousticCalculatorEngine.BAR_SCALE_MAX,
        );

        // Run initial calculations
        this.calculateSolutions();
        this.updateUrlHash();

        // Watch for changes and recalculate
        this.$watch(
          () => this.settings,
          () => {
            this.calculateSolutions();
            this.saveSettings();
            this.updateUrlHash();
          },
          { deep: true },
        );

        this.$watch("settings.selected_product", () => {
          this.syncSelectedProductSettings();
        });

        // Recalculate when the user drags the reverb-bar thumb.
        this.$watch("desiredRT60", (val) => {
          var max = window.AcousticCalculatorEngine.BAR_SCALE_MAX;
          if (val < 0) this.desiredRT60 = 0;
          else if (val > max) this.desiredRT60 = max;
          this.calculateSolutions();
        });

        // Reset thumb to recommended level when the lookup-table target changes
        // (e.g. user picks a different room type).
        this.$watch("targetRT60", (newVal) => {
          this.desiredRT60 = Math.min(
            newVal,
            window.AcousticCalculatorEngine.BAR_SCALE_MAX,
          );
        });

        // Recalculate super-user RT60 when product quantities or room params change.
        this.$watch(
          () =>
            this.superUserProducts.map((p) => p.qty).join(",") +
            "|" +
            this.settings.room_length +
            "|" +
            this.settings.room_width +
            "|" +
            this.settings.room_height +
            "|" +
            JSON.stringify(this.settings.walls) +
            "|" +
            this.settings.ceiling_material +
            "|" +
            this.settings.floor_material +
            "|" +
            this.settings.furniture +
            "|" +
            this.settings.open_entryways,
          () => {
            this.calculateSuperUserRT60();
          },
          { immediate: true },
        );

        this.measureReverbLabelLayout();
        this._reverbResizeHandler = () => {
          clearTimeout(this._reverbResizeTimer);
          this._reverbResizeTimer = setTimeout(
            () => this.measureReverbLabelLayout(),
            150,
          );
        };
        window.addEventListener("resize", this._reverbResizeHandler);
      },

      updated() {
        this.measureReverbLabelLayout();
      },

      beforeUnmount() {
        this.teardownCommercialCalculator();
      },

      unmounted() {
        this.teardownCommercialCalculator();
      },
    };
  }

  // Initialize when DOM is ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      initializeApp();
      // Signal that commercial calculator is ready
      window.acousticCalculatorCommercialReady = true;
    });
  } else {
    initializeApp();
    // Signal that commercial calculator is ready
    window.acousticCalculatorCommercialReady = true;
  }
})();
